// Offline drafts + outbox, stored in IndexedDB and scoped per user id.
// Migrated from localStorage (legacy keys are moved on first access).
import { idbGet, idbSet, idbDel, idbKeys, idbClear } from "@/lib/idb";
import { logWarn } from "@/lib/log";
import { uploadThumb } from "@/lib/imageThumbs";

const draftKey = (uid, tid) => `draft_${uid}_${tid}`;
const outboxKey = (uid) => `outbox_${uid}`;

let migrated = false;
async function migrateLegacy() {
  if (migrated) return;
  migrated = true;
  try {
    const legacy = Object.keys(localStorage).filter((k) => k.startsWith("hakina_draft_") || k.startsWith("hakina_outbox_"));
    for (const k of legacy) {
      const v = JSON.parse(localStorage.getItem(k));
      await idbSet(k.replace("hakina_", ""), v);
      localStorage.removeItem(k);
    }
  } catch (e) { logWarn("[offline] legacy migration skipped:", e); }
}

export const loadDraft = async (uid, tid) => {
  await migrateLegacy();
  try { return (await idbGet(draftKey(uid, tid))) || null; } catch { return null; }
};

export const saveDraft = async (uid, tid, data) => {
  try {
    await idbSet(draftKey(uid, tid), { ...data, savedAt: new Date().toISOString() });
    return true;
  } catch { return false; }
};

export const clearDraft = (uid, tid) => idbDel(draftKey(uid, tid)).catch(() => {});

export const getOutbox = async (uid) => {
  await migrateLegacy();
  try { return (await idbGet(outboxKey(uid))) || []; } catch { return []; }
};

const setOutbox = (uid, items) => idbSet(outboxKey(uid), items);

export const outboxCount = async (uid) => {
  try { return (await getOutbox(uid)).length; } catch { return 0; }
};

export const queueSubmission = async (uid, payload) => {
  const items = await getOutbox(uid);
  await setOutbox(uid, [...items, { id: payload.id || crypto.randomUUID(), ...payload, queued_at: new Date().toISOString() }]);
};

// Offline review sign-offs queue alongside submissions and flush the same way.
export const queueSignoff = (uid, data) => queueSubmission(uid, { kind: "signoff", ...data });

// Retry-safe: deterministic storage path + upsert, and an "already signed"
// RPC response is treated as sent.
async function sendSignoff(supabase, item) {
  const path = item.signature_path || `${item.company_id}/signoffs/${item.id}.png`;
  if (!item.signature_path) {
    const blob = await dataUrlToBlob(item.dataUrl);
    const { error } = await supabase.storage.from("submission-photos")
      .upload(path, blob, { contentType: "image/png", upsert: true });
    if (error) throw error;
  }
  const { error } = await supabase.rpc("sign_assignment_signoff", { p_signoff: item.signoff_id, p_signature_path: path });
  if (error && !/already|duplicate/i.test(error.message || "")) throw error;
}

// Purge helpers (logout / user switch): tenant data must never linger.
// Last-known-good caches (localStorage) so a signed-in user keeps working
// fully offline: auth snapshot, feature map, template schemas, form context.
// TENANT ISOLATION: data snapshots are namespaced by the ACTING COMPANY so a
// membership change can never replay another company's cached data on this
// device. Only the auth bootstrap snapshot (needed before the acting company
// is known) and the already-company-keyed "shared" entries are exempt.
let cacheCompanyScope = "boot";
export const setCacheScope = (companyId) => { cacheCompanyScope = companyId || "none"; };
const SCOPE_EXEMPT = new Set(["auth"]);
const cacheKey = (uid, key) =>
  uid === "shared" || SCOPE_EXEMPT.has(key)
    ? `hakina_cache_${uid}_${key}`
    : `hakina_cache_${uid}_${cacheCompanyScope}_${key}`;
// Remove cached snapshots for companies the user no longer belongs to, plus
// LEGACY un-namespaced keys from before company-scoping (they are unreadable
// by current code, but a stale device must not carry another tenant's data).
export const purgeStaleCompanyCaches = (uid, activeCompanyIds) => {
  try {
    const keep = new Set(activeCompanyIds || []);
    const prefix = `hakina_cache_${uid}_`;
    for (let i = localStorage.length - 1; i >= 0; i--) {
      const k = localStorage.key(i);
      if (!k || !k.startsWith(prefix)) continue;
      const rest = k.slice(prefix.length);
      if (SCOPE_EXEMPT.has(rest)) continue;
      const scope = rest.slice(0, 36); // uuid-length company scope
      if (/^[0-9a-f]{8}-[0-9a-f]{4}/.test(scope)) {
        if (!keep.has(scope)) localStorage.removeItem(k); // ex-member company
      } else {
        localStorage.removeItem(k); // legacy un-namespaced snapshot
      }
    }
  } catch (e) { logWarn("[offline] cache purge failed:", e); }
};
export const cachePut = (uid, key, data) => {
  try { localStorage.setItem(cacheKey(uid, key), JSON.stringify(data)); } catch (e) { logWarn("[offline] cachePut failed:", e); }
};
export const cacheGet = (uid, key) => {
  try { return JSON.parse(localStorage.getItem(cacheKey(uid, key))); } catch { return null; }
};

// Airplane mode can make the client THROW (severed fetch) instead of
// returning { error } — normalize so snapshot fallbacks always run.
export const safeQuery = async (q) => {
  try { return await q; } catch (e) { return { data: null, error: e }; }
};
export const safeAll = (qs) => Promise.all(qs.map(safeQuery));

// Offline-aware template lookup for flag-to-escalate actions: live query
// first, then the same cached snapshots the Field Forms picker reads.
export const findActionTemplate = async (supabase, uid, type, companyId) => {
  let tpls = null;
  try {
    const { data } = await supabase.from("templates").select("id,company_id,type,status").eq("type", type).eq("status", "published");
    tpls = data;
  } catch (e) { logWarn("[offline] action template query failed, using cache:", e); }
  if (!tpls || !tpls.length) {
    const cached = [
      ...(((cacheGet(uid, "forms") || {}).templates) || []),
      ...(((cacheGet(uid, "home") || {}).templates) || []),
    ];
    tpls = cached.filter((t) => t.type === type && t.status === "published");
  }
  return tpls.find((x) => x.company_id === companyId) || tpls[0] || null;
};

export const clearAllLocalData = async () => {
  try { await idbClear(); } catch (e) { logWarn("[offline] idbClear failed:", e); }
  try {
    Object.keys(localStorage)
      .filter((k) => k.startsWith("hakina_draft_") || k.startsWith("hakina_outbox_") || k.startsWith("hakina_cache_"))
      .forEach((k) => localStorage.removeItem(k));
  } catch (e) { logWarn("[offline] localStorage purge failed:", e); }
};

export const clearOtherUsersData = async (keepUid) => {
  try {
    const keys = await idbKeys();
    for (const k of keys) {
      if (typeof k === "string" && !k.includes(keepUid)) await idbDel(k);
    }
    Object.keys(localStorage)
      .filter((k) => k.startsWith("hakina_cache_") && !k.includes(keepUid))
      .forEach((k) => localStorage.removeItem(k));
  } catch (e) { logWarn("[offline] clearOtherUsersData failed:", e); }
};

const dataUrlToBlob = async (dataUrl) => (await fetch(dataUrl)).blob();

// Upload photo answers to storage, then insert the submission row.
// Idempotent: payload carries a client-generated id — a duplicate insert
// (double flush, retry after partial failure) hits the PK and is treated as sent.
export async function submitPayload(supabase, payload) {
  const { queued_at: _q, quick_comments: quickComments = [], contractor_signoffs: signoffs = [], ...row } = payload;
  const answers = { ...row.answers };
  const photoFieldIds = [];
  (row.template_schema?.sections || []).forEach((s) =>
    (s.fields || []).forEach((fl) => { if (fl.type === "photo") photoFieldIds.push(fl.id); })
  );
  if (Array.isArray(answers._qa_photos) && answers._qa_photos.length) photoFieldIds.push("_qa_photos");

  for (const fid of photoFieldIds) {
    const photos = answers[fid];
    if (!Array.isArray(photos) || photos.length === 0) continue;
    const uploaded = [];
    for (const p of photos) {
      if (p.path) { uploaded.push(p); continue; } // already uploaded
      const blob = await dataUrlToBlob(p.dataUrl);
      const safeName = (p.name || "photo").replace(/[^a-zA-Z0-9._-]/g, "_");
      const path = `${row.company_id}/${crypto.randomUUID()}-${safeName}`;
      const { error } = await supabase.storage.from("submission-photos")
        .upload(path, blob, { contentType: p.type || "image/jpeg" });
      if (error) throw error;
      await uploadThumb(supabase, path, blob); // PDF exports read this instead of the original
      uploaded.push({ ...p, path, dataUrl: undefined, name: p.name, type: p.type });
    }
    // Strip inline data + temporary signed URLs — the immutable record keeps only the storage path.
    answers[fid] = uploaded.map(({ dataUrl: _d, signedUrl: _s, ...rest }) => rest);
  }

  // Contractor sign-off signatures live in company-scoped storage (never inline).
  for (const so of signoffs) {
    if (so.signature_path || !so.dataUrl) continue;
    const blob = await dataUrlToBlob(so.dataUrl);
    const path = `${row.company_id}/signoffs/${crypto.randomUUID()}.png`;
    const { error } = await supabase.storage.from("submission-photos")
      .upload(path, blob, { contentType: "image/png" });
    if (error) throw error;
    so.signature_path = path; // retry-safe: no re-upload after partial failure
  }

  const { data, error } = await supabase.from("submissions")
    .insert({ ...row, answers }).select("id").single();
  const alreadySent = error && (error.code === "23505" || /duplicate key/i.test(error.message || ""));
  if (error && !alreadySent) throw error;
  // Contractor sign-offs ride along (immutable, audited via trigger).
  if (signoffs.length) {
    const soRows = signoffs.map((so) => ({
      id: so.id, company_id: row.company_id, submission_id: row.id,
      contractor_id: so.contractor_id || null, contractor_name: so.name,
      contractor_company: so.company || null, contact: so.contact || null,
      signature_path: so.signature_path, signed_at: so.signed_at,
    }));
    const { error: se } = await supabase.from("contractor_signoffs").insert(soRows);
    if (se && !(se.code === "23505" || /duplicate key/i.test(se.message || ""))) throw se;
  }
  // Urgent submissions notify supervisors/admins (email + SMS): auto-flagged
  // forms AND action items raised via the flag pill. Best-effort,
  // fire-and-forget — the server is idempotent per submission id.
  if ((row.flagged || row.parent_submission_id) && !alreadySent) {
    try {
      const { data: s } = await supabase.auth.getSession();
      const token = s?.session?.access_token;
      if (token) {
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/alerts/flagged/${row.id}`, {
          method: "POST",
          headers: { Authorization: `Bearer ${token}` },
        }).catch(() => {});
      }
    } catch (e) { logWarn("[alerts] flagged-alert trigger failed (non-blocking):", e); }
  }
  // Quick-action comments ride along (immutable, audited via trigger).
  if (quickComments.length) {
    const rows = quickComments.map((c) => ({
      id: c.id, company_id: row.company_id, submission_id: row.id,
      section_id: c.section_id || null, section_title: c.section_title || null,
      author: row.submitted_by, author_name: row.worker_name,
      body: c.body, created_at: c.created_at,
    }));
    const { error: ce } = await supabase.from("submission_comments").insert(rows);
    if (ce && !(ce.code === "23505" || /duplicate key/i.test(ce.message || ""))) throw ce;
  }
  // Revision flow: mark the original as superseded (audited via trigger).
  if (row.revision_of) {
    await supabase.rpc("mark_superseded", { sub: row.revision_of });
  }
  return data || { id: row.id };
}

// Try to send everything queued while offline. Returns { sent, remaining }.
// Locked: concurrent calls (StrictMode double-effects, online event + mount,
// foreground event) share one flush instead of double-submitting the queue.
let flushing = null;
export function flushOutbox(supabase, uid) {
  if (flushing) return flushing;
  flushing = (async () => {
    const items = await getOutbox(uid);
    if (!items.length) return { sent: 0, remaining: 0 };
    const failed = [];
    let sent = 0;
    for (const item of items) {
      try {
        if (item.kind === "signoff") await sendSignoff(supabase, item);
        else await submitPayload(supabase, item);
        sent += 1;
      }
      catch { failed.push(item); }
    }
    await setOutbox(uid, failed);
    return { sent, remaining: failed.length };
  })().finally(() => { flushing = null; });
  return flushing;
}
